import { z } from 'zod';
import { isValidTimezone } from './dates.js';
import { ANIMAL_KINDS, BIOMES, DIFFICULTIES, SEED_KINDS, STARTER_BIOMES } from './types.js';

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const text = z.string().trim().min(1).max(200);
/** A tree label, or '' for none. Length is checked against the rules' labelMax. */
const label = z.string().trim().max(40);
const id = z.string().min(1).max(64);
const tags = z.array(z.string().trim().min(1).max(40)).max(20);

export const repeatSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('weekly'), days: z.array(z.number().int().min(0).max(6)).min(1).max(7) }),
  z.object({ kind: z.literal('everyNDays'), every: z.number().int().min(1).max(365), startDate: day }),
]);

export const bedSpotSchema = z.object({ where: z.literal('bed'), plot: z.number().int().min(0) });
export const groundSpotSchema = z.object({
  where: z.literal('ground'),
  biome: z.enum(BIOMES),
  x: z.number().int(),
  y: z.number().int(),
});
export const spotSchema = z.discriminatedUnion('where', [bedSpotSchema, groundSpotSchema]);
export type Spot = z.infer<typeof spotSchema>;
export type GroundSpot = z.infer<typeof groundSpotSchema>;

const common = {
  /** Optional id chosen by the device, so a task made offline keeps its id once it syncs. */
  id: z.string().uuid().optional(),
  text,
  notes: z.string().max(2000).optional(),
  difficulty: z.enum(DIFFICULTIES).optional(),
  tags: tags.optional(),
  sectionId: id.nullable().optional(),
};

export const newTaskSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('habit'), ...common, up: z.boolean().optional(), down: z.boolean().optional() }),
  z.object({
    type: z.literal('daily'),
    ...common,
    label: label.optional(),
    repeat: repeatSchema.optional(),
    checklist: z.array(text).max(50).optional(),
  }),
  z.object({
    type: z.literal('todo'),
    ...common,
    dueDate: day.optional(),
    checklist: z.array(text).max(50).optional(),
  }),
]);
export type NewTask = z.infer<typeof newTaskSchema>;

export const taskPatchSchema = z
  .object({
    text: text.optional(),
    notes: z.string().max(2000).optional(),
    difficulty: z.enum(DIFFICULTIES).optional(),
    tags: tags.optional(),
    up: z.boolean().optional(),
    down: z.boolean().optional(),
    label: label.optional(),
    repeat: repeatSchema.optional(),
    paused: z.boolean().optional(),
    dueDate: day.nullable().optional(),
  })
  .strict();
export type TaskPatch = z.infer<typeof taskPatchSchema>;

export const actionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('createTask'), task: newTaskSchema }),
  z.object({ type: z.literal('updateTask'), id, patch: taskPatchSchema }),
  /** Tasks are archived, never deleted: they keep their history and their plants. */
  z.object({ type: z.literal('archiveTask'), id }),
  z.object({ type: z.literal('unarchiveTask'), id }),
  z.object({ type: z.literal('moveTask'), id, sectionId: id.nullable().optional(), position: z.number().int().min(0) }),
  /** Habits: up is +, down is −. Dailies and to-dos: up completes, down undoes. */
  z.object({ type: z.literal('scoreTask'), id, direction: z.enum(['up', 'down']) }),
  z.object({ type: z.literal('addChecklistItem'), taskId: id, text }),
  z.object({ type: z.literal('toggleChecklistItem'), id }),
  z.object({ type: z.literal('removeChecklistItem'), id }),
  z.object({ type: z.literal('createSection'), name: z.string().trim().min(1).max(60) }),
  z.object({ type: z.literal('renameSection'), id, name: z.string().trim().min(1).max(60) }),
  z.object({ type: z.literal('deleteSection'), id }),
  /** Trees go on open ground once; habit plants go in the bed; buds can move until they bloom. */
  z.object({ type: z.literal('placePlant'), plantId: id, spot: spotSchema }),
  z.object({ type: z.literal('pickBloom'), plantId: id, use: z.enum(['coins', 'bouquet', 'press']) }),
  z.object({ type: z.literal('pullWeed'), plantId: id }),
  z.object({ type: z.literal('plantSeed'), seed: z.enum(SEED_KINDS), spot: groundSpotSchema }),
  z.object({
    type: z.literal('buyAnimal'),
    kind: z.enum(ANIMAL_KINDS),
    homePlantId: id.optional(),
    biome: z.enum(BIOMES).optional(),
    name: text.optional(),
  }),
  z.object({ type: z.literal('renameAnimal'), id, name: text }),
  /** Pauses everything; `until` is the first day back, or leave it out to pause until resumed. */
  z.object({ type: z.literal('pause'), until: day.optional() }),
  z.object({ type: z.literal('resume') }),
  /** Replaces the player's scenery changes for a biome (moved pieces and the bank). The scene must be unlocked. */
  z.object({
    type: z.literal('editScene'),
    biome: z.enum(BIOMES),
    moves: z.record(z.string().max(16), z.object({ x: z.number().int().min(-64).max(448), y: z.number().int().min(-64).max(320) })).refine((m) => Object.keys(m).length <= 400, 'Too many moved pieces'),
    hidden: z.array(z.string().max(16)).max(400),
  }),
  /**
   * Picks the starting place. Free while the garden is untouched; once something grows there,
   * `confirm` is required and everything planted in the old starter is left behind (trees start
   * over as seeds, wildflowers and flowers are lost, tree companions leave; the bed and its
   * companions ride along).
   */
  z.object({ type: z.literal('chooseStarter'), biome: z.enum(STARTER_BIOMES), confirm: z.boolean().optional() }),
  z.object({
    type: z.literal('updateSettings'),
    timezone: z.string().refine(isValidTimezone, 'Unknown time zone').optional(),
    dayStartHour: z.number().int().min(0).max(23).optional(),
  }),
]);
export type Action = z.infer<typeof actionSchema>;
